import React, { useMemo, useState } from 'react';
import { ChevronLeft, Check, Gift, MailOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { useAtom } from 'jotai';
import referralHero from '../../assets/referral-hero.png';
import SpinnerLoading from '../common/SpinnerLoading';
import { getMyReferralCode } from '../../queryfunctions/queryfunctions';
import { useRequestProcessor } from '../../requestProcessor';
import { activeChildDetail } from '../../atomstore/store';

const TERMS_URL = 'https://www.footprintseducation.in/privacy-policy';

const cardClass =
    'rounded-xl bg-white border border-gray-200/80 shadow-sm';

const ReferralCode = () => {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [activeChild] = useAtom(activeChildDetail);

    const { useQueryWrapper } = useRequestProcessor();
    const referralQuery = useQueryWrapper(['getMyReferralCode', activeChild?.id], () => getMyReferralCode(activeChild?.id), {
        enabled: !!activeChild?.id,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    });

    const { referralCode, referrerCredit, refereeDiscount, expiryDate, apiErrorMessage } = useMemo(() => {
        const payload = referralQuery?.data;

        if (!payload) {
            return {
                referralCode: '',
                referrerCredit: '',
                refereeDiscount: '',
                expiryDate: '',
                apiErrorMessage: '',
            };
        }

        const status = payload?.status;
        const isSuccess =
            status === 'success' ||
            status === 'ok' ||
            status === true ||
            payload?.success === true;

        if (isSuccess) {
            const d = payload.data ?? payload ?? {};
            return {
                referralCode: d.coupon_code || d.referral_code || d.referralCode || '',
                referrerCredit: d.refferal_amount_to_parent ?? '',
                refereeDiscount: d.refferal_amount_to_friend ?? '',
                expiryDate: d.coupon_expiry_date || '',
                apiErrorMessage: '',
            };
        }

        if (payload.status === 'error' || payload?.error) {
            return {
                referralCode: '',
                referrerCredit: '',
                refereeDiscount: '',
                expiryDate: '',
                apiErrorMessage: payload.error || payload.message || 'Unable to fetch referral code.',
            };
        }

        return {
            referralCode: '',
            referrerCredit: '',
            refereeDiscount: '',
            expiryDate: '',
            apiErrorMessage: payload?.error || payload?.message || '',
        };
    }, [referralQuery?.data]);

    const SHARE_MESSAGE = useMemo(() => {
        const code = referralCode || 'your referral code';
        const raw = refereeDiscount != null && refereeDiscount !== '' ? String(refereeDiscount).trim() : '';
        const rewardAmount = raw ? (raw.startsWith('₹') ? raw : `₹${raw}`) : '₹0';
        return `Looking for a great preschool experience? Join the Footprints Preschool family!

Use my referral link to admission and get an instant ${rewardAmount} discount on your monthly fees.

🎁 Your Reward: ${rewardAmount} Off
🎫 Referral Code: ${code}
📲 Get Started: https://www.footprintseducation.in/`;
    }, [referralCode, refereeDiscount]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(referralCode || '');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = referralCode || '';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleShareWithFriends = async () => {
        try {
            if (Capacitor.isNativePlatform()) {
                await Share.share({
                    text: SHARE_MESSAGE,
                    dialogTitle: 'Share with friends',
                });
                return;
            }
            if (typeof navigator !== 'undefined' && navigator.share) {
                await navigator.share({
                    text: SHARE_MESSAGE,
                });
                return;
            }
            await navigator.clipboard.writeText(SHARE_MESSAGE);
        } catch (e) {
            if (e && (e.message === 'Share canceled' || e.message === 'AbortError' || e.name === 'AbortError')) {
                return;
            }
            console.warn('Share failed', e);
        }
    };

    const openTerms = () => {
        window.open(TERMS_URL, '_blank', 'noopener,noreferrer');
    };

    return (
        <div
            className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto"
        >
            {(referralQuery.isLoading || referralQuery.isFetching) && <SpinnerLoading />}
            {/* Header (match Profile/Photo Gallery pattern) */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="active:scale-95 transition"
                    aria-label="Go back"
                >
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>
                    Admission Referral Coupon
                </h2>
            </div>

            <div className="flex flex-col gap-2 p-3 lg:gap-4 lg:p-4">
                {/* Hero — short strip on phone (~100dvh fit); taller from md up */}
                <div className={`${cardClass} overflow-hidden max-lg:shrink min-h-0`}>
                    <div className="bg-[#f5f5f4] w-full overflow-hidden leading-[0] h-[88px] sm:h-[104px] md:h-[260px] lg:h-[288px]">
                        <img
                            src={referralHero}
                            alt="Illustration: parents and children — refer and connect at Footprints"
                            className="w-full h-full object-cover object-top block"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                    <div className="px-3 py-2 lg:px-4 lg:py-4">
                        <h2 className="text-[15px] lg:text-[17px] font-bold text-gray-900 leading-snug">
                            Refer a Parent, Save on Fees!
                        </h2>
                        <p className="text-[11px] lg:text-[13px] text-gray-700 mt-1 lg:mt-2 leading-snug lg:leading-relaxed">
                            Invite a friend and you both get rewarded when your referred friend successfully
                            enrolls their child.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-4 max-lg:shrink-0">
                    {/* Referrer reward */}
                    <div className={`${cardClass} p-3 flex gap-2.5 items-start h-full lg:p-4 lg:gap-3.5`}>
                        <div className="w-10 h-10 lg:w-[52px] lg:h-[52px] rounded-xl bg-gradient-to-br from-[#7c3aed] to-[#5b21b6] flex items-center justify-center flex-shrink-0 shadow-inner">
                            <Gift className="w-5 h-5 lg:w-7 lg:h-7 text-white" strokeWidth={1.75} />
                        </div>
                        <div className="min-w-0 pt-0.5">
                            <p className="font-bold text-gray-900 text-[13px] lg:text-[14px] leading-snug">
                                You Get: {referrerCredit || '—'} Referral Credit
                            </p>
                            <p className="text-[11px] lg:text-[12px] text-gray-600 mt-1 lg:mt-1.5 leading-snug lg:leading-relaxed line-clamp-2 lg:line-clamp-none">
                                Earn a {referrerCredit || '—'} Referral Credit on the upcoming monthly invoice.
                            </p>
                        </div>
                    </div>

                    {/* Friend reward — open letter */}
                    <div className={`${cardClass} p-3 flex gap-2.5 items-start h-full lg:p-4 lg:gap-3.5`}>
                        <div className="w-10 h-10 lg:w-[52px] lg:h-[52px] rounded-xl bg-gradient-to-br from-[#38bdf8] to-[#0284c7] flex items-center justify-center flex-shrink-0 shadow-inner">
                            <MailOpen className="w-5 h-5 lg:w-7 lg:h-7 text-white" strokeWidth={1.75} />
                        </div>
                        <div className="min-w-0 pt-0.5">
                            <p className="font-bold text-gray-900 text-[13px] lg:text-[14px] leading-snug">
                                Your Friend Gets: {refereeDiscount || '—'} Discount on Admission
                            </p>
                            <p className="text-[11px] lg:text-[12px] text-gray-600 mt-1 lg:mt-1.5 leading-snug lg:leading-relaxed line-clamp-2 lg:line-clamp-none">
                                Your referred friend receives a{' '}
                                <span className="font-semibold text-gray-900">{refereeDiscount || '—'} discount</span>{' '}
                                on admission fee.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Referral code */}
                <div className={`${cardClass} p-3 lg:p-4 max-lg:shrink-0`}>
                    <p className="text-[12px] lg:text-[13px] text-gray-500 mb-2 lg:mb-3">Your Referral Code</p>
                    {apiErrorMessage ? (
                        <p className="text-[13px] text-red-600">{apiErrorMessage}</p>
                    ) : null}
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap lg:gap-3">
                        <p className="flex-1 min-w-0 text-[16px] lg:text-[18px] font-bold text-gray-900 tracking-wide break-all">
                            {referralCode || '—'}
                        </p>
                        <button
                            type="button"
                            onClick={handleCopy}
                            disabled={!referralCode}
                            className={`flex-shrink-0 px-4 py-2 lg:px-6 lg:py-2.5 rounded-full text-[13px] lg:text-[14px] font-semibold text-white shadow-sm transition-colors ${!referralCode
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : copied
                                        ? 'bg-green-500'
                                        : 'bg-[#f97316] active:bg-[#ea580c]'
                                }`}
                        >
                            {copied ? (
                                <span className="flex items-center gap-1.5">
                                    <Check className="w-4 h-4" />
                                    Copied
                                </span>
                            ) : (
                                'Copy'
                            )}
                        </button>
                    </div>
                    {expiryDate ? (
                        <p className="text-[11px] lg:text-[12px] text-gray-500 mt-2">
                            Valid till: {expiryDate}
                        </p>
                    ) : null}
                </div>

                <div className="mt-auto lg:mt-0 flex flex-col gap-2 lg:gap-3">
                    <button
                        type="button"
                        onClick={openTerms}
                        className="w-full shrink-0 text-center text-[12px] lg:text-[13px] font-medium text-[#00BCD4] underline underline-offset-[3px] decoration-[#00BCD4] py-0.5 lg:py-1"
                    >
                        Term and Conditions link.
                    </button>

                    <button
                        type="button"
                        onClick={handleShareWithFriends}
                        disabled={!referralCode}
                        className="w-full shrink-0 py-2.5 lg:py-3.5 rounded-xl text-[14px] lg:text-[15px] font-bold text-white bg-[#00BCD4] shadow-md active:opacity-90 transition-opacity border border-[#00acc1]/30"
                    >
                        Share with Friends
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReferralCode;
